import React, { useMemo, useState } from 'react';
import { Card, Form, Select, Typography, Row, Col, Grid, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../../../components/common/StandardTable';
import { useGetVwQaqcPipeNdtPerformance, useGetVwQaqcPipeNdtPerformanceByKind } from '../../../../../hooks/genericHooks';
import type { VwQaqcPipeNdtPerformance, VwQaqcPipeNdtPerformanceByKind } from '../../../../../types';

const { Title } = Typography;

function QaqcPipeNdtPerformanceList() {
  const screens = Grid.useBreakpoint();
  const { data, isLoading } = useGetVwQaqcPipeNdtPerformance();
  const { data: byKindData, isLoading: byKindLoading } = useGetVwQaqcPipeNdtPerformanceByKind();
  const rows = (data?.data ?? []) as VwQaqcPipeNdtPerformance[];
  const byKind = (byKindData?.data ?? []) as VwQaqcPipeNdtPerformanceByKind[];

  const [svcFilter, setSvcFilter] = useState<string | undefined>(undefined);
  const [fluidFilter, setFluidFilter] = useState<string | undefined>(undefined);

  const svcOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach(r => { if (r.serviceclass_name) s.add(r.serviceclass_name); });
    return Array.from(s).sort().map(v => ({ label: v, value: v }));
  }, [rows]);

  const fluidOptions = useMemo(() => {
    const s = new Set<string>();
    rows.filter(r => !svcFilter || r.serviceclass_name === svcFilter)
        .forEach(r => { if (r.fluid) s.add(r.fluid); });
    return Array.from(s).sort().map(v => ({ label: v, value: v }));
  }, [rows, svcFilter]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const a = !svcFilter || r.serviceclass_name === svcFilter;
      const b = !fluidFilter || r.fluid === fluidFilter;
      return a && b;
    });
  }, [rows, svcFilter, fluidFilter]);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = useMemo(() => {
    if (!selectedKey) return null as null | VwQaqcPipeNdtPerformance;
    const [pid, svc, fld] = selectedKey.split('|');
    return filtered.find(r => String(r.project_id) === pid && r.serviceclass_name === svc && r.fluid === fld) || null;
  }, [selectedKey, filtered]);

  const detailRows = useMemo(() => {
    if (!selected) return [] as (VwQaqcPipeNdtPerformanceByKind & { key: string })[];
    const arr = byKind.filter(k => k.project_id === selected.project_id && k.serviceclass_name === selected.serviceclass_name && k.fluid === selected.fluid);
    return arr.map((r, i) => ({ key: `${r.weld_kind}|${i}`, ...r }));
  }, [selected, byKind]);

  const pct = (num?: number | null, den?: number | null) => {
    if (!den || den <= 0 || !num) return null;
    return (num * 100) / den;
  };

  const fmtPct = (v: number | null) => (v == null ? '' : `${v.toFixed(1)}%`);

  const columns: ColumnsType<VwQaqcPipeNdtPerformance & { key: string; }> = [
    { title: 'Service Class', dataIndex: 'serviceclass_name', key: 'serviceclass_name', width: 130 },
    { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 80 },
    { title: 'Total', dataIndex: 'total_welds', key: 'total_welds', width: 80, align: 'right' },
    { title: 'Made', dataIndex: 'made_welds', key: 'made_welds', width: 80, align: 'right' },
    {
      title: 'PT', key: 'pt', children: [
        { title: 'Appl.', dataIndex: 'pt_applicable', key: 'pt_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'pt_done_count', key: 'pt_done_count', width: 80, align: 'right' },
        { title: '%', key: 'pt_pct_col', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.pt_done_count, r.pt_applicable)),
        },
      ]
    },
    {
      title: 'RT', key: 'rt', children: [
        { title: 'Appl.', dataIndex: 'rt_applicable', key: 'rt_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'rt_done_count', key: 'rt_done_count', width: 80, align: 'right' },
        { title: '%', key: 'rt_pct_col', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.rt_done_count, r.rt_applicable)),
        },
      ]
    },
    {
      title: 'MT', key: 'mt', children: [
        { title: 'Appl.', dataIndex: 'mt_applicable', key: 'mt_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'mt_done_count', key: 'mt_done_count', width: 80, align: 'right' },
        { title: '%', key: 'mt_pct_col', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.mt_done_count, r.mt_applicable)),
        },
      ]
    },
    {
      title: 'UT', key: 'ut', children: [
        { title: 'Appl.', dataIndex: 'ut_applicable', key: 'ut_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'ut_done_count', key: 'ut_done_count', width: 80, align: 'right' },
        { title: '%', key: 'ut_pct_col', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.ut_done_count, r.ut_applicable)),
        },
      ]
    },
    {
      title: 'PMI', key: 'pmi', children: [
        { title: 'Appl.', dataIndex: 'pmi_applicable', key: 'pmi_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'pmi_done_count', key: 'pmi_done_count', width: 80, align: 'right' },
        { title: '%', key: 'pmi_pct_col', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.pmi_done_count, r.pmi_applicable)),
        },
      ]
    },
    {
      title: 'PWHT', key: 'pwht', children: [
        { title: 'Appl.', dataIndex: 'pwht_applicable', key: 'pwht_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'pwht_done_count', key: 'pwht_done_count', width: 80, align: 'right' },
        { title: '%', key: 'pwht_pct_col', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.pwht_done_count, r.pwht_applicable)),
        },
      ]
    },
  ];

  const kindColumns: ColumnsType<VwQaqcPipeNdtPerformanceByKind & { key: string; }> = [
    { title: 'Weld Kind', dataIndex: 'weld_kind', key: 'weld_kind', width: 80 },
    { title: 'Total', dataIndex: 'total_welds', key: 'total_welds', width: 80, align: 'right' },
    { title: 'Made', dataIndex: 'made_welds', key: 'made_welds', width: 80, align: 'right' },
    {
      title: 'PT', key: 'pt_k', children: [
        { title: 'Appl.', dataIndex: 'pt_applicable', key: 'pt_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'pt_done_count', key: 'pt_done_count', width: 80, align: 'right' },
        { title: '%', key: 'pt_pct_col_k', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.pt_done_count, r.pt_applicable)),
        },
      ]
    },
    {
      title: 'RT', key: 'rt_k', children: [
        { title: 'Appl.', dataIndex: 'rt_applicable', key: 'rt_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'rt_done_count', key: 'rt_done_count', width: 80, align: 'right' },
        { title: '%', key: 'rt_pct_col_k', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.rt_done_count, r.rt_applicable)),
        },
      ]
    },
    {
      title: 'MT', key: 'mt_k', children: [
        { title: 'Appl.', dataIndex: 'mt_applicable', key: 'mt_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'mt_done_count', key: 'mt_done_count', width: 80, align: 'right' },
        { title: '%', key: 'mt_pct_col_k', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.mt_done_count, r.mt_applicable)),
        },
      ]
    },
    {
      title: 'UT', key: 'ut_k', children: [
        { title: 'Appl.', dataIndex: 'ut_applicable', key: 'ut_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'ut_done_count', key: 'ut_done_count', width: 80, align: 'right' },
        { title: '%', key: 'ut_pct_col_k', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.ut_done_count, r.ut_applicable)),
        },
      ]
    },
    {
      title: 'PMI', key: 'pmi_k', children: [
        { title: 'Appl.', dataIndex: 'pmi_applicable', key: 'pmi_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'pmi_done_count', key: 'pmi_done_count', width: 80, align: 'right' },
        { title: '%', key: 'pmi_pct_col_k', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.pmi_done_count, r.pmi_applicable)),
        },
      ]
    },
    {
      title: 'PWHT', key: 'pwht_k', children: [
        { title: 'Appl.', dataIndex: 'pwht_applicable', key: 'pwht_applicable', width: 80, align: 'right' },
        { title: 'Done', dataIndex: 'pwht_done_count', key: 'pwht_done_count', width: 80, align: 'right' },
        { title: '%', key: 'pwht_pct_col_k', width: 70, align: 'right',
          render: (_, r) => fmtPct(pct(r.pwht_done_count, r.pwht_applicable)),
        },
      ]
    },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={6}>
          <Title level={4} style={{ margin: 0 }}>NDT Performance</Title>
        </Col>
        <Col xs={24} md={18}>
          <Form layout="vertical" style={{ display: 'inline-flex', flexWrap: screens.lg ? 'wrap' : 'nowrap', gap: 8, alignItems: 'flex-end' }}>
            <Form.Item label="Service Class" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '16ch' }}
                placeholder={undefined}
                options={svcOptions}
                value={svcFilter}
                onChange={setSvcFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
            <Form.Item label="Fluid" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '12ch' }}
                placeholder={undefined}
                options={fluidOptions}
                value={fluidFilter}
                onChange={setFluidFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <StandardTable
        loading={isLoading}
        dataSource={filtered.map((r, idx) => ({ key: `${r.project_id}|${r.serviceclass_name}|${r.fluid}|${idx}` , ...r }))}
        columns={columns}
        rowKey="key"
        bordered
        scroll={{ x: 'max-content' }}
        onRow={(record) => ({
          onClick: () => {
            const k = `${record.project_id}|${record.serviceclass_name}|${record.fluid}`;
            setSelectedKey(prev => prev === k ? null : k);
          }
        })}
      />

      <Divider style={{ margin: '12px 0' }} />

      <Card size="small" title={selected ? `Breakdown (BW/FW/SP) — ${selected.serviceclass_name} • ${selected.fluid}` : 'Breakdown (BW/FW/SP) — select a row'}>
        <StandardTable
          loading={byKindLoading}
          dataSource={detailRows}
          columns={kindColumns}
          rowKey="key"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={false}
        />
      </Card>
    </Card>
  );
}

export default QaqcPipeNdtPerformanceList;
